import { makeAutoObservable } from 'mobx';

import { MobxManager } from 'framework/mobx-integration';
import { DynamicValueStore, TextWidgetConfig } from './types';
import { createNode } from 'tools/helpers/dom';
import { getDynamicDataType } from 'controls/designer/features/widgets/textWidget/utils/getAllDynamicDataTypes';

export class TextWidgetStore {
	renderedContent = '';

	private stores: { id: string; store: DynamicValueStore }[] = [];
	private mobx = new MobxManager();

	constructor(public config: TextWidgetConfig) {
		makeAutoObservable(this);

		this.initStores();

		this.mobx.reaction(
			() => this.stores.map((s) => s.store.value),
			() => this.render(),
			{ fireImmediately: true },
		);
	}

	private initStores() {
		const entries = this.config.dynamicEntries ?? [];
		this.stores = entries.flatMap((entry) => {
			const desc = getDynamicDataType(entry.type);
			if (!desc) return [];
			return [{ id: entry.id, store: desc.getStore(entry) }];
		});
	}

	setTooltipForSpan = async (span: HTMLElement) => {
		const id = span.getAttribute('data-id');
		const entry = this.config.dynamicEntries?.find((e) => e.id === id);
		const tooltip = entry && (await getDynamicDataType(entry.type)?.getTooltip?.(entry));
		if (tooltip) {
			span.title = tooltip;
		}
	};

	render = () => {
		if (!this.config.text) {
			this.renderedContent = '';
			return;
		}
		if (!this.stores.length) {
			this.renderedContent = this.config.text;
			return;
		}

		const root = createNode('<div>' + this.config.text + '</div>');
		this.stores.forEach(({ id, store }) => {
			const node = root.querySelector(`[data-id="${id}"]`);
			if (node) node.textContent = store.value;
		});
		this.renderedContent = root.innerHTML;
	};

	destroy() {
		this.mobx.destroy();
		this.stores.forEach((s) => s.store.destroy());
	}
}
